/**
 * Runtime-validated client environment.
 *
 * We parse `import.meta.env` through Zod so misconfiguration explodes at
 * module load time instead of silently breaking API calls at runtime.
 *
 * Only `VITE_*`-prefixed vars are exposed by Vite to the browser bundle.
 * Never put secrets here — every value in this file ships to the client.
 */

import { z } from 'zod';

const envSchema = z.object({
  VITE_API_BASE_URL: z
    .string()
    .url({ message: 'VITE_API_BASE_URL must be a valid URL' })
    .transform((s) => s.replace(/\/+$/, '')),
  VITE_TON_NETWORK: z.enum(['mainnet', 'testnet']).default('testnet'),
  VITE_DEV_INIT_DATA: z.string().optional(),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  // Surfacing this loudly in the console is the best we can do in the
  // browser — there is no process.exit. React will still fail to mount
  // because the `env` export below will throw on access.
  console.error('[env] Invalid client environment:', parsed.error.flatten());
  throw new Error('Invalid client environment. Check your .env files.');
}

export const env = Object.freeze(parsed.data);
export type Env = typeof env;
